/**
 * Avis sur MyEquip (propriétaire, 24/10/2026), comme le bouton de Claude :
 * signaler un problème ou proposer une idée en quelques secondes, sans quitter
 * l'écran. L'avis part à l'équipe MyEquip avec la page où l'on est, la version
 * et la taille d'écran ; le serveur en fait une issue GitHub
 * (docs/integrations/github-avis.md). Ouvert depuis l'icône à côté du profil
 * (ordinateur), le Menu (téléphone), la page Aide et les erreurs de chargement.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  PRODUCT_FEEDBACK_KINDS, PRODUCT_FEEDBACK_MAX, PRODUCT_FEEDBACK_MIN, type ProductFeedbackKind,
} from '@myequip/shared';
import { api } from '../../core/api.js';
import { useAuth } from '../../core/auth.js';
import { t } from '../../core/i18n.js';
import { ICONS } from '../../design/icons.js';
import { APP_VERSION } from '../shell-extras.js';
import { Button, Field } from '../ui.js';
import { ChoiceGroup } from '../ui/ChoiceGroup.js';
import { Banner } from '../ui/Feedback.js';
import { Sheet } from '../ui/Sheet.js';

/** Page d'où part l'avis : chemin et paramètres seuls, bornés comme le serveur les garde. */
function contextPage(pathname: string, search: string): string | undefined {
  const page = `${pathname}${search}`;
  return page.length <= 200 && /^\/(?!\/)\S*$/.test(page) ? page : pathname.slice(0, 200) || undefined;
}

function FeedbackSheet(props: { initialKind: ProductFeedbackKind; onClose: () => void }): ReactNode {
  const { me } = useAuth();
  const location = useLocation();
  const [kind, setKind] = useState<ProductFeedbackKind>(props.initialKind);
  const [message, setMessage] = useState('');
  // Même clé tant que le contenu est le même : un nouvel essai après une réponse perdue rend le même avis.
  const attempt = useRef<{ content: string; key: string } | undefined>(undefined);
  const page = contextPage(location.pathname, location.search);
  const send = useMutation({
    mutationFn: () => {
      const content = JSON.stringify([kind, message.trim(), page ?? '']);
      if (attempt.current?.content !== content) attempt.current = { content, key: crypto.randomUUID() };
      return api<{ id: string; number: string }>('/product-feedback', {
        method: 'POST',
        body: {
          kind,
          message: message.trim(),
          ...(page ? { context_page: page } : {}),
          context_app: `Web ${APP_VERSION} · ${window.innerWidth}×${window.innerHeight}`,
          idempotencyKey: attempt.current.key,
        },
      });
    },
  });

  if (send.data) {
    return (
      <Sheet
        kicker={t('avisProduit.kicker')}
        title={t('avisProduit.merci')}
        description={t('avisProduit.transmis', { numero: send.data.number })}
        onClose={props.onClose}
        footer={<Button onClick={props.onClose}>{t('commun.fermer')}</Button>}
      >
        {null}
      </Sheet>
    );
  }

  const tooShort = message.trim().length < PRODUCT_FEEDBACK_MIN;
  return (
    // ui-allow ui/no-form-in-modal: avis en un geste demandé par le propriétaire (24/10/2026), comme le bouton de Claude : un type et un message, sans quitter l'écran
    <Sheet
      kicker={t('avisProduit.kicker')}
      title={t('avisProduit.titre')}
      description={t('avisProduit.description')}
      onClose={props.onClose}
      footer={me?.support_session ? (
        <Button variant="secondary" onClick={props.onClose}>{t('commun.fermer')}</Button>
      ) : (
        <>
          <Button onClick={() => send.mutate()} disabled={tooShort || send.isPending}>
            {send.isPending ? t('avisProduit.envoi') : t('avisProduit.envoyer')}
          </Button>
          <Button variant="secondary" onClick={props.onClose}>{t('commun.annuler')}</Button>
        </>
      )}
    >
      {me?.support_session ? (
        <Banner>{t('avisProduit.assistance')}</Banner>
      ) : (
        <>
          <ChoiceGroup<ProductFeedbackKind>
            label={t('avisProduit.type')}
            variant="segmented"
            value={kind}
            onChange={setKind}
            options={PRODUCT_FEEDBACK_KINDS.map((value) => ({ value, label: t(`avisProduit.types.${value}`) }))}
          />
          <Field
            label={t('avisProduit.message')}
            hint={t('avisProduit.aide', { compte: message.length, max: PRODUCT_FEEDBACK_MAX })}
          >
            <textarea
              rows={5}
              value={message}
              maxLength={PRODUCT_FEEDBACK_MAX}
              placeholder={t(`avisProduit.exemples.${kind}`)}
              onChange={(event) => setMessage(event.target.value)}
            />
          </Field>
          {send.error ? <Banner tone="danger">{(send.error as Error).message}</Banner> : null}
        </>
      )}
    </Sheet>
  );
}

/** Ouvre la feuille d'avis ; `sheet` est à rendre là où le bouton l'ouvre. */
export function useProductFeedback(): { open: (kind?: ProductFeedbackKind) => void; sheet: ReactNode } {
  const [openKind, setOpenKind] = useState<ProductFeedbackKind | undefined>(undefined);
  const open = useCallback((kind: ProductFeedbackKind = 'bug') => setOpenKind(kind), []);
  return {
    open,
    sheet: openKind ? <FeedbackSheet initialKind={openKind} onClose={() => setOpenKind(undefined)} /> : null,
  };
}

/** Icône à côté du profil, en pied de colonne (ordinateur), comme celle de Claude. */
export function ProductFeedbackButton(): ReactNode {
  const feedback = useProductFeedback();
  return (
    <>
      <button
        type="button"
        className="side-nav-feedback"
        aria-label={t('avisProduit.ouvrir')}
        title={t('avisProduit.ouvrir')}
        aria-haspopup="dialog"
        onClick={(event) => {
          event.currentTarget.focus();
          feedback.open();
        }}
      >
        <ICONS.signaler size={18} aria-hidden="true" />
      </button>
      {feedback.sheet}
    </>
  );
}
